import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Instagram, Linkedin, Mail, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSwitch } from "@/components/LanguageSwitch";
import { t } from "@/i18n";

/* The header and footer that wrap every public page. The planner keeps its own
   chrome: once someone is inside their wedding, a navigation bar offering to
   read the blog is noise. */

const NAV = [
  { to: "/", label: "nav.home", end: true },
  { to: "/blog", label: "nav.journal" },
  { to: "/support", label: "nav.help" },
];

export const SiteHeader = ({ brandName, tagline }) => (
  <header className="border-b border-[color:var(--line)]">
    <nav className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-between gap-3 px-6 py-5">
      {/* The name is a brand, not a description — it says nothing about seating
          plans on its own. The line beside it does that job, so a first-time
          visitor knows what this is without scrolling. */}
      <Link to="/" className="flex items-baseline gap-2.5">
        <span className="font-display text-xl font-semibold tracking-tight">
          {brandName || t("app.name")}
        </span>
        <span
          className="hidden text-[12.5px] sm:inline"
          style={{ color: "var(--muted-ink)" }}
        >
          {tagline || t("app.tagline")}
        </span>
      </Link>

      <div className="flex flex-wrap items-center gap-1.5">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`site-nav-${item.to.replace("/", "") || "home"}`}
            className="rounded-full px-3 py-1.5 text-[13px] transition-colors"
            style={({ isActive }) => ({
              backgroundColor: isActive ? "var(--paper-2)" : "transparent",
              color: isActive ? "var(--ink)" : "var(--ink-soft)",
            })}
          >
            {t(item.label)}
          </NavLink>
        ))}
        <LanguageSwitch className="ml-1" />
        <Button asChild size="sm" className="ml-1 rounded-full text-[13px]">
          <Link to="/create" data-testid="site-nav-create">
            {t("site.tryIt")}
          </Link>
        </Button>
      </div>
    </nav>
  </header>
);

const SOCIALS = [
  { key: "youtube", Icon: Youtube, href: (v) => v },
  { key: "instagram", Icon: Instagram, href: (v) => v },
  { key: "linkedin", Icon: Linkedin, href: (v) => v },
  { key: "email", Icon: Mail, href: (v) => `mailto:${v}` },
];

export const SiteFooter = ({ site }) => {
  const social = site?.social || {};
  const disclosure = site?.affiliates?.enabled ? site?.affiliates?.disclosure : "";

  return (
    <footer className="mt-20 border-t border-[color:var(--line)] bg-[color:var(--paper-2)]">
      <div className="mx-auto max-w-[1180px] px-6 py-10">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <p className="font-display text-lg">{site?.brand?.name || t("app.name")}</p>
            <p className="mt-1 max-w-md text-[12.5px] text-[color:var(--muted-ink)]">
              {site?.brand?.tagline || t("app.tagline")}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {SOCIALS.filter(({ key }) => (social[key] || "").trim()).map(({ key, Icon, href }) => (
              <a
                key={key}
                href={href(social[key])}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={key}
                data-testid={`social-${key}`}
                className="rounded-full border p-2 transition-colors hover:bg-white"
                style={{ borderColor: "var(--line)" }}
              >
                <Icon className="h-4 w-4" style={{ color: "var(--ink-soft)" }} />
              </a>
            ))}
          </div>
        </div>

        {disclosure ? (
          <p
            className="mt-8 max-w-3xl text-[11.5px] leading-relaxed"
            style={{ color: "var(--muted-ink)" }}
            data-testid="affiliate-disclosure"
          >
            {disclosure}
          </p>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11.5px] text-[color:var(--muted-ink)]">
            {t("landing.footer")}
          </p>
          <Link
            to="/support"
            className="text-[11.5px] text-[color:var(--muted-ink)] hover:underline"
            data-testid="footer-support-link"
          >
            {t("nav.help")}
          </Link>
          {/* Deliberately understated: the owner knows it is here, and it is
              behind a password anyway. */}
          <Link
            to="/studio"
            className="text-[11.5px] text-[color:var(--muted-ink)] hover:underline"
            data-testid="studio-link"
          >
            {t("site.ownerArea")}
          </Link>
        </div>
      </div>
    </footer>
  );
};
